import React, { useEffect, useMemo, useRef } from "react";
import { Spring } from "react-spring/renderprops";
import { List } from "react-virtualized";
import styled, { css, keyframes } from "styled-components";
import Pagination from "@charty/pagination";
import { content } from "@glossy";
import { usePages, useSize } from "@lkf/hooks";
import { ChartProps } from "../model";
import Infoline from "./gauge";

const GaugeContainer = styled.div<{
  height: string;
  hasBorderBottom?: boolean;
}>`
  max-height: ${(props) => props.height};
  overflow: hidden !important;
  border-top: 1px solid ${(props) => content(props, "tertiary")};
  border-bottom: ${(props) =>
    props.hasBorderBottom && `1px solid ${content(props, "tertiary")}`};
`;

const StyledPagination = styled(Pagination)`
  margin-top: 16px;
`;

const appear = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const StyledInfoline = styled(Infoline)<{
  index: number;
  delay: number;
  animate?: boolean;
}>`
  border-bottom: 1px solid ${(props) => content(props, "tertiary")};
  margin-top: -2px;
  animation: ${(props) =>
    props.animate &&
    css`
      ${appear} ${props.delay}ms ease
    `};
`;

const HorizontalStackedBarChart = ({
  locale,
  mainUnit,
  tooltip,
  onClickGauge,
  isFullscreen,
  limit,
  hovered,
  filteredGauges,
  fullTotal,
  sortedGauges,
  metric,
  chartValuesFormat,
  maxCount,
  isPNG,
}: ChartProps) => {
  const ref = useRef();
  const { width } = useSize(ref);
  const lineHeight = 53;
  const animationDelay = 800;
  const length = filteredGauges.length;
  const showPagination = !isFullscreen && !isPNG && length > limit;
  const { page, onPageReset, onPageUp, onPageDown } = usePages(length, limit);

  const listHeight = useMemo(() => {
    if (isFullscreen) {
      return lineHeight * sortedGauges.length;
    }
    return lineHeight * limit;
  }, [isFullscreen, limit, sortedGauges.length]);

  useEffect(() => {
    onPageReset();
  }, [onPageReset, filteredGauges]);

  function rowRenderer({
    key,
    index,
    style,
    isScrolling,
  }: {
    key: string;
    index: number;
    style: any;
    isScrolling: boolean;
  }) {
    return (
      <div key={key} style={style}>
        <StyledInfoline
          maxCount={maxCount}
          isScrolling={isScrolling}
          index={index + 1}
          gauge={sortedGauges[index]}
          locale={locale}
          mainUnit={mainUnit}
          width={width}
          total={fullTotal}
          delay={(index + 1) * animationDelay}
          onClickGauge={onClickGauge}
          hovered={hovered}
          tooltip={tooltip}
          metric={metric}
          chartValuesFormat={chartValuesFormat}
        />
      </div>
    );
  }
  return (
    <div ref={ref}>
      <GaugeContainer
        height={isFullscreen ? "100%" : `${lineHeight * limit}px`}
        hasBorderBottom={sortedGauges.length >= limit}
      >
        <Spring from={{ top: 0 }} to={{ top: lineHeight * (page.from - 1) }}>
          {(props) => (
            <List
              width={1}
              height={listHeight}
              rowCount={sortedGauges.length}
              rowHeight={lineHeight}
              rowRenderer={rowRenderer}
              containerStyle={{
                width: "100%",
                maxWidth: "100%",
              }}
              style={{
                width: "100%",
                overflow: "hidden",
              }}
              scrollTop={props.top}
            />
          )}
        </Spring>
      </GaugeContainer>
      {showPagination && (
        <StyledPagination
          length={length}
          page={page}
          locale={locale}
          onPageUp={onPageUp}
          onPageDown={onPageDown}
        />
      )}
    </div>
  );
};

export default HorizontalStackedBarChart;
